import React, { useState, useEffect } from "react";
import axiosInstance from "../utlis/axiosInstance";
import ConnectionsForm from "../components/ConnectionsForm";
import styles from "./GroupManagement.module.css";
import { useUser } from "../provider/UserProvider";
import {
  Business as BusinessIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

interface Group {
  id: number;
  name: string;
  parent_group_id: number | null;
  attributes?: {
    type?: string;
    location?: string;
    [key: string]: any;
  };
  disabled?: boolean;
  children?: Group[];
  hasChildren?: boolean;
  depth?: number;
}

const GroupManagement: React.FC = () => {
  const { permissions } = useUser();

  // Check if user has permission to view groups
  const canViewGroups = permissions?.includes("GROUP_LIST");
  const canUpdateGroups = permissions?.includes("GROUP_UPDATE");

  const [mode, setMode] = useState<"list" | "add" | "edit">("list");
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupName, setGroupName] = useState("");
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const [groupType, setGroupType] = useState("");
  const [groupLocation, setGroupLocation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingGroupId, setDeletingGroupId] = useState<number | null>(null);

  const fetchGroups = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get("/gpstracker/grouphandler/");

      if (response.data?.status && response.data?.responseData) {
        setGroups(response.data.responseData);
      } else {
        setGroups([]);
      }
      setMode("list");
    } catch (error: any) {
      console.error("Error fetching groups:", error);
      setError("Failed to fetch groups");
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleAddGroup = () => {
    setMode("add");
    setGroupName("");
    setSelectedParentId(null);
    setSelectedGroup(null);
    setGroupType("");
    setGroupLocation("");
  };

  const handleEditGroup = (group: Group) => {
    setSelectedGroup(group);
    setGroupName(group.name);
    setSelectedParentId(group.parent_group_id);
    setGroupType(group.attributes?.type || "");
    setGroupLocation(group.attributes?.location || "");
    setMode("edit");
  };

  const wouldCreateCycle = (
    groupId: number,
    newParentId: number | null
  ): boolean => {
    if (!newParentId) return false;
    if (groupId === newParentId) return true;

    const parentGroup = groups.find((g) => g.id === newParentId);
    if (!parentGroup) return false;

    let currentParentId = parentGroup.parent_group_id;
    while (currentParentId) {
      if (currentParentId === groupId) return true;
      const nextParent = groups.find((g) => g.id === currentParentId);
      if (!nextParent) break;
      currentParentId = nextParent.parent_group_id;
    }
    return false;
  };

  const getAvailableParentGroups = (currentGroupId?: number): Group[] => {
    return groups?.filter((group) => {
      if (currentGroupId && group.id === currentGroupId) return false;
      if (currentGroupId && wouldCreateCycle(currentGroupId, group.id))
        return false;
      return true;
    });
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) return;

    setIsSaving(true);
    try {
      if (
        mode === "edit" &&
        selectedGroup &&
        wouldCreateCycle(selectedGroup.id, selectedParentId)
      ) {
        setError("Cannot set this parent group as it would create a cycle in the hierarchy");
        return;
      }

      const groupData = {
        name: groupName.trim(),
        parent_group_id: selectedParentId,
        attributes: {
          type: groupType.trim() || undefined,
          location: groupLocation.trim() || undefined,
        },
      };

      if (mode === "edit" && selectedGroup) {
        await axiosInstance.put(
          `/gpstracker/grouphandler/${selectedGroup.id}`,
          groupData
        );
      } else {
        await axiosInstance.post("/gpstracker/grouphandler/", groupData);
      }

      await fetchGroups();
    } catch (error: any) {
      console.error("Error saving group:", error);
      setError("Failed to save group");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    const group = groups.find((g) => g.id === groupId);
    const hasChildren = groups.some((g) => g.parent_group_id === groupId);

    if (hasChildren) {
      setError("Cannot delete group with sub-groups");
      return;
    }

    setDeletingGroupId(groupId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingGroupId) return;

    try {
      await axiosInstance.delete(`/gpstracker/grouphandler/${deletingGroupId}`);
      await fetchGroups();
    } catch (error: any) {
      console.error("Error deleting group:", error);
      setError("Failed to delete group");
    } finally {
      setDeleteDialogOpen(false);
      setDeletingGroupId(null);
    }
  };

  const buildTreeStructure = (items: Group[]): Group[] => {
    const itemMap: { [key: number]: Group } = {};
    const roots: Group[] = [];

    items?.forEach((item) => {
      itemMap[item.id] = { ...item, children: [], hasChildren: false };
    });

    items?.forEach((item) => {
      if (!item.parent_group_id) {
        roots.push(itemMap[item.id]);
      } else {
        const parent = itemMap[item.parent_group_id];
        if (parent) {
          parent.children?.push(itemMap[item.id]);
          parent.hasChildren = true;
        }
      }
    });

    return roots;
  };

  const flattenTree = (nodes: Group[], depth = 0): Group[] => {
    return nodes.reduce((flat: Group[], node) => {
      const shouldShowChildren = expandedRows.has(node.id);
      return [
        ...flat,
        { ...node, depth },
        ...(shouldShowChildren && node.children
          ? flattenTree(node.children, depth + 1)
          : []),
      ];
    }, []);
  };

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filterGroups = (groups: Group[], searchTerm: string): Group[] => {
    if (!searchTerm.trim()) return groups;

    const searchLower = searchTerm.toLowerCase().trim();

    return groups.filter((group) => {
      const nameMatch = group.name.toLowerCase().includes(searchLower);
      const typeMatch = group.attributes?.type
        ?.toLowerCase()
        .includes(searchLower);
      const locationMatch = group.attributes?.location
        ?.toLowerCase()
        .includes(searchLower);

      return nameMatch || typeMatch || locationMatch;
    });
  };

  const getFilteredGroups = (): Group[] => {
    return filterGroups(groups, search);
  };

  const treeData = buildTreeStructure(getFilteredGroups());
  const flattenedData = flattenTree(treeData);

  const renderGroupItem = (item: Group) => {
    const depth = item.depth || 0;
    const hasChildren = item.hasChildren;
    const isExpanded = expandedRows.has(item.id);
    const childCount = item.children?.length || 0;

    // Check permissions for each action
    const canUpdate = permissions?.includes("GROUP_UPDATE");
    const canDelete = permissions?.includes("GROUP_DELETE");

    return (
      <div
        key={item.id}
        className={styles.groupItem}
        style={{ marginLeft: `${depth * 24}px` }}
      >
        <div className={styles.groupItemContent}>
          <div className={styles.groupInfo}>
            {hasChildren && (
              <button
                onClick={() => toggleRow(item.id)}
                className={styles.expandButton}
              >
                {isExpanded ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
              </button>
            )}
            <div className={styles.groupAvatar}>
              <GroupIcon sx={{ fontSize: 20, color: 'white' }} />
            </div>
            <div className={styles.groupDetails}>
              <div className={styles.groupName}>
                {item.name}
                {hasChildren && ` (${childCount})`}
              </div>
              {item.attributes &&
                (item.attributes.type || item.attributes.location) && (
                  <div className={styles.groupSubtitle}>
                    {item.attributes.type && `${item.attributes.type}`}
                    {item.attributes.type &&
                      item.attributes.location &&
                      " • "}
                    {item.attributes.location &&
                      `${item.attributes.location}`}
                  </div>
                )}
            </div>
          </div>
          <div className={styles.groupActions}>
            {deletingGroupId === item.id ? (
              <div className={styles.loadingSpinner}></div>
            ) : (
              <>
                <button
                  onClick={() => {
                    setSelectedGroupId(item.id.toString());
                    setConnectionModalOpen(true);
                  }}
                  className={`${styles.actionButton} ${styles.linkButton}`}
                  title="Link"
                >
                  <LinkIcon sx={{ fontSize: 16 }} />
                </button>
                <button
                  onClick={() => handleEditGroup(item)}
                  disabled={!canUpdate}
                  className={`${styles.actionButton} ${styles.editButton}`}
                  title="Edit"
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </button>
                <button
                  onClick={() => handleDeleteGroup(item.id)}
                  disabled={!canDelete}
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  title="Delete"
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!canViewGroups) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.mainCard}>
            <div className={styles.cardContent}>
              <div style={{ textAlign: 'center', color: '#dc2626', fontSize: '1.2rem', fontWeight: 600 }}>
                You don't have permission to view groups.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading && groups.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.mainCard}>
            <div className={styles.cardContent}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <div className={styles.loadingText}>Loading groups...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
          
              <div>
                <div className={styles.headerTitle}>
                  Group Management
                </div>
                <div className={styles.headerSubtitle}>
                  Organize and manage your organizational structure
                </div>
              </div>
            </div>
            <button
              onClick={handleAddGroup}
              disabled={isSaving || !canUpdateGroups}
              className={styles.createButton}
            >
              <AddIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Create Group
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className={styles.alert}>
            <WarningIcon sx={{ fontSize: 20, mr: 0.5 }} />
            {error}
          </div>
        )}

      
        {/* Table Section */}
        {flattenedData.length > 0 && (
          <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
              <div className={styles.tableTitle}>
                Group Directory ({flattenedData.length})
              </div>
              <div className={styles.searchContainer}>
              <div className={styles.searchField}>
                <SearchIcon className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search groups by name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.table}>
                <thead className={styles.tableHead}>
                  <tr>
                    <th>Group Name</th>
                    <th>Type</th>
                    <th>Location</th>
                    <th>Parent Group</th>
                    <th>Sub-groups</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {flattenedData.map((item) => {
                    const parentGroup = groups.find(g => g.id === item.parent_group_id);
                    const childCount = item.children?.length || 0;
                    const canUpdate = permissions?.includes("GROUP_UPDATE");
                    const canDelete = permissions?.includes("GROUP_DELETE");

                    return (
                      <tr key={item.id}>
                        <td>
                          <div className={styles.groupNameCell}>
                            <div className={styles.tableGroupAvatar}>
                              <GroupIcon sx={{ fontSize: 18, color: 'white' }} />
                            </div>
                            <span className={styles.groupNameText}>{item.name}</span>
                          </div>
                        </td>
                        <td className={item.attributes?.type ? '' : styles.emptyCell}>
                          {item.attributes?.type || '-'}
                        </td>
                        <td className={item.attributes?.location ? '' : styles.emptyCell}>
                          {item.attributes?.location || '-'}
                        </td>
                        <td className={parentGroup?.name ? '' : styles.emptyCell}>
                          {parentGroup?.name || '-'}
                        </td>
                        <td className={styles.subGroupsCell}>
                          {childCount > 0 ? `${childCount} sub-groups` : 'No sub-groups'}
                        </td>
                        <td>
                          <span className={`${styles.statusBadge} ${item.disabled ? styles.statusInactive : styles.statusActive}`}>
                            {item.disabled ? 'Inactive' : 'Active'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button
                              onClick={() => {
                                setSelectedGroupId(item.id.toString());
                                setConnectionModalOpen(true);
                              }}
                              className={`${styles.tableActionButton} ${styles.tableLinkButton}`}
                              title="Link"
                            >
                              <LinkIcon sx={{ fontSize: 14 }} />
                            </button>
                            <button
                              onClick={() => handleEditGroup(item)}
                              disabled={!canUpdate}
                              className={`${styles.tableActionButton} ${styles.tableEditButton}`}
                              title="Edit"
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                            </button>
                            <button
                              onClick={() => handleDeleteGroup(item.id)}
                              disabled={!canDelete}
                              className={`${styles.tableActionButton} ${styles.tableDeleteButton}`}
                              title="Delete"
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        {mode !== "list" && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <h2>{mode === "edit" ? "Edit Group" : "Add Group"}</h2>
                <button
                  onClick={() => setMode("list")}
                  className={styles.closeButton}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
              <div className={styles.modalContent}>
                <div className={styles.formField}>
                  <label>Group Name *</label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Enter group name"
                    disabled={isSaving}
                    maxLength={32}
                  />
                </div>
                <div className={styles.formField}>
                  <label>Group Type</label>
                  <input
                    type="text"
                    value={groupType}
                    onChange={(e) => setGroupType(e.target.value)}
                    placeholder="e.g., fleet, department"
                    disabled={isSaving}
                    maxLength={32}
                  />
                </div>
                <div className={styles.formField}>
                  <label>Location</label>
                  <input
                    type="text"
                    value={groupLocation}
                    onChange={(e) => setGroupLocation(e.target.value)}
                    placeholder="e.g., New York, San Francisco"
                    disabled={isSaving}
                    maxLength={32}
                  />
                </div>
                <div className={styles.formField}>
                  <label>Parent Group</label>
                  <select
                    value={selectedParentId || ""}
                    onChange={(e) => setSelectedParentId(e.target.value ? Number(e.target.value) : null)}
                    disabled={isSaving}
                  >
                    <option value="">None</option>
                    {getAvailableParentGroups(
                      mode === "edit" ? selectedGroup?.id : undefined
                    ).map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.modalActions}>
                <button
                  onClick={() => setMode("list")}
                  disabled={isSaving}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveGroup}
                  disabled={isSaving || !groupName.trim()}
                  className={styles.saveButton}
                >
                  {isSaving ? "Saving..." : mode === "edit" ? "Save Changes" : "Add Group"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Connections Modal */}
        {selectedGroupId && (
          <ConnectionsForm
            id={selectedGroupId}
            open={connectionModalOpen}
            onClose={() => {
              setConnectionModalOpen(false);
              setSelectedGroupId(null);
            }}
            type="groupId"
          />
        )}

        {/* Delete Confirmation Modal */}
        {deleteDialogOpen && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <h2 style={{ color: '#dc2626' }}>Confirm Delete</h2>
              </div>
              <div className={styles.modalContent}>
                <p>Are you sure you want to delete this group? This action cannot be undone.</p>
              </div>
              <div className={styles.modalActions}>
                <button
                  onClick={() => setDeleteDialogOpen(false)}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className={styles.deleteButton}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupManagement;